"use client";

/**
 * React provider + hook for the active display size.
 *
 * Parallels `LocaleProvider`: it owns the current `DisplaySize`, persists the
 * user's choice to localStorage, and writes the matching `data-display-size`
 * attribute onto `<html>` so the CSS zoom variants in `globals.css` apply.
 *
 * The render-blocking snippet in the root layout (`displaySizeNoFlashScript`)
 * sets the attribute before first paint; this provider re-syncs on mount and
 * is the source of truth for live changes (the settings switcher), so the size
 * updates without a reload.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import {
  DEFAULT_DISPLAY_SIZE,
  applyDisplaySize,
  resolveClientDisplaySize,
  writeStoredDisplaySize,
  type DisplaySize,
} from "@/lib/display-size";

type DisplaySizeCtx = {
  displaySize: DisplaySize;
  setDisplaySize: (next: DisplaySize) => void;
};

const Ctx = createContext<DisplaySizeCtx | null>(null);

export function DisplaySizeProvider({ children }: { children: React.ReactNode }) {
  const [displaySize, setSize] = useState<DisplaySize>(DEFAULT_DISPLAY_SIZE);

  // Re-resolve on mount: the SSR markup renders the default attribute, and the
  // no-flash script already applied any stored preference to the DOM before
  // paint — this brings React state in line with it.
  useEffect(() => {
    const resolved = resolveClientDisplaySize();
    setSize(resolved);
    applyDisplaySize(resolved);
  }, []);

  const setDisplaySize = useCallback((next: DisplaySize) => {
    setSize(next);
    writeStoredDisplaySize(next);
    applyDisplaySize(next);
  }, []);

  const value = useMemo<DisplaySizeCtx>(
    () => ({ displaySize, setDisplaySize }),
    [displaySize, setDisplaySize],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Current display size + setter. Falls back to a safe no-op default when no
 *  provider is mounted (public/login surfaces), so consumers never crash. */
export function useDisplaySize(): DisplaySizeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return { displaySize: DEFAULT_DISPLAY_SIZE, setDisplaySize: () => { /* no provider */ } };
  }
  return ctx;
}
